import ColorHash from "color-hash";
import isEqual from "lodash/isEqual";

import {
  Action,
  ActionType,
  LayoutOrientation,
  isAddClipToImagePanelAction,
  isAddPathToMapPanelAction,
  isAddSeriesToPlotPanelAction,
  isCreatePanelAction,
  isMovePanelAction,
  isPutImagePanelDataAction,
  isPutFilesAction,
  isRemoveAllPanelsAction,
  isRemovePanelAction,
  isRemovePathFromMapPanelAction,
  isRemoveSeriesFromPlotPanelAction,
  isReplaceStateAction,
  isResizeLayoutsAction,
  isSetAllLayoutsResizingAction,
  isSetDataSourceAction,
  isSetDatasourcesAction,
  isSetEventVisibilityAction,
  isSetVisibilityForAllEventsAction,
  isSetWorkspaceSettingsAction,
  isSetImagePanelConfigAction,
  isSetLayoutResizingAction,
  isSetLogPanelConfigAction,
  isSetPathCoordinateUnitsAction,
  isSetPathStyleAction,
  isSetPathVisibilityAction,
  isSetSeriesConfigAction,
  isSetSeriesVisibilityAction,
  isAddDataToLogPanelAction,
  isAddDataToRawMessagePanelAction,
  isRemoveMessagePathFromLogPanelAction,
  isRemoveMessagePathFromRawMessagePanelAction,
  isRemoveClipFromImagePanelAction,
  isReplacePanelsAndLayoutAction,
  isSetPlotPanelConfigAction,
  isSetPanelTitleAction,
  isSetThreeDPanelConfigAction,
  isAddThreeDEntityAction,
  isUpdateThreeDEntityAction,
  isSetThreeDModelAction,
  isRemoveThreeDEntityAction,
  isRenameThreeDEntityAction,
  isSetDocumentPanelFileAction,
  isCreateDocumentPanelWithFileAction,
} from "../actions";
import { assignSeriesColor } from "../assignSeriesColor";
import {
  INITIAL_STATE,
  DEFAULT_IMAGE_PANEL_CONFIG,
  DEFAULT_PLOT_PANEL_CONFIG,
} from "../context";
import { cyclingColorScale } from "../CyclingColorScale";
import { selectLayoutById } from "../hooks";
import {
  idForClip,
  idForMap,
  idForSeries,
  isSameDataSource,
} from "../identifiers";
import { makeMapPath } from "../mapPath";
import {
  type DocumentPanelState,
  type LayoutItem,
  isDocumentPanelState,
  isImagePanelState,
  isLayoutItem,
  isLogPanelState,
  isMapPanelState,
  isPlotPanelState,
  isRawMessagePanelState,
  isThreeDPanelState,
  LayoutType,
  PanelType,
  PlotSeries,
  State,
  TopicData,
} from "../schema";
import {
  ImagePanelClip,
  MapPath,
  PlotAxisConfig,
  PlotPanelConfig,
  PlotPanelState,
  ThreeDPanelState,
  PlotSeriesConfig,
  PlotYAxis,
  ImagePanelState,
} from "../schema/v1";
import {
  DEFAULT_ENTITY_ID,
  DEFAULT_GRID,
  descendantIds,
  type Entity as ThreeDEntity,
  type EntityRegistry,
} from "../schema/v1/three-d";

import { addItemToLayout } from "./addItemToLayout";
import { removeItemFromLayout } from "./removeItemFromLayout";
import { setIsLayoutResizing } from "./setIsLayoutResizing";
import { updateLayout } from "./updateLayout";

const colorHash = new ColorHash({ lightness: 0.5, saturation: 0.5 });

function titleForClips(data: ImagePanelClip[]): string {
  if (data.length === 0) {
    return "";
  }

  const firstTopicName = data[0].data.topic.name;

  let topicPart = "Multiple topics";

  // If every clip has the same topic name
  if (data.every((clip) => clip.data.topic.name === firstTopicName)) {
    topicPart = firstTopicName;
  }

  if (data.length === 1) {
    return topicPart;
  } else {
    return `${topicPart} (${data.length} clips)`;
  }
}

function titleForPlot(data: PlotSeries[]): string {
  if (data.length === 0) {
    return "";
  }

  if (data.length === 1) {
    const series = data[0];
    return `${series.data.topic.name}.${series.data.messagePath.dotPath}`;
  }

  // Multiple series from one topic (the common case: several signals of one
  // sensor) titles as that topic rather than the misleading "Multiple topics".
  const firstTopicName = data[0].data.topic.name;
  if (data.every((series) => series.data.topic.name === firstTopicName)) {
    return `${firstTopicName} (${data.length} signals)`;
  }

  return "Multiple topics";
}

function titleForMap(data: MapPath[]): string {
  if (data.length === 0) {
    return "";
  }

  // If there's 1 path
  if (data.length === 1 && data[0].data.length > 0) {
    const path = data[0];
    return `${path.data[0].topic.name}`;
  }

  // If there are multiple paths
  return "Multiple paths";
}

function titleForLogPanel(data: TopicData[]): string {
  if (data.length === 0) {
    return "";
  }
  if (data.length === 1) {
    return `${data[0].topic.name}.${data[0].messagePath.dotPath}`;
  }
  return data[0].topic.name;
}

function titleForRawMessagePanel(data: TopicData[]): string {
  if (data.length === 0) {
    return "";
  }
  if (data.length === 1) {
    return `${data[0].topic.name}.${data[0].messagePath.dotPath}`;
  }
  return data[0].topic.name;
}

const defaultMapPathColor = "#007cbf";

/**
 * The panel's entity registry, defaulting to one derived from its components.
 *
 * The registry is the authority on which entities exist, but it was introduced
 * after the panel shipped — so a workspace saved earlier has components with no
 * registry beside them. Deriving a flat, parentless registry from the component
 * keys makes such a panel behave exactly as it did before the hierarchy existed,
 * rather than reading as a panel with no entities at all.
 */
function entityRegistryOf(panel: ThreeDPanelState): EntityRegistry {
  const declared = panel.config?.entities;
  if (declared && Object.keys(declared).length > 0) {
    return declared;
  }
  return Object.fromEntries(
    Object.keys(panel.data.entities).map((id) => [id, {}]),
  );
}

export function reducer<T>(state: State, action: Action<T>): State {
  switch (action.type) {
    case ActionType.AddClipToImagePanel: {
      if (!isAddClipToImagePanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const id = idForClip();

      const clip: ImagePanelClip = {
        data: action.payload.data,
        id,
      };

      const nextData = [...panel.data, clip];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForClips(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddDataToLogPanel: {
      if (!isAddDataToLogPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      const nextData = [...panel.data, action.payload.data];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddDataToRawMessagePanel: {
      if (!isAddDataToRawMessagePanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isRawMessagePanelState(panel)) {
        return state;
      }

      if (panel.data.some((d) => isSameDataSource(d, action.payload.data))) {
        return state;
      }

      if (
        panel.data.length > 0 &&
        panel.data[0].topic.id !== action.payload.data.topic.id
      ) {
        return state;
      }

      const nextData = [...panel.data, action.payload.data];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForRawMessagePanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddPathToMapPanel: {
      if (!isAddPathToMapPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }

      const id = idForMap(action.payload.data);

      if (panel.data.some((path) => path.id === id)) {
        return state;
      }

      // First path keeps the brand default; later paths get a deterministic
      // per-id color, so a path keeps its color when other paths are removed.
      const lineColor =
        panel.data.length > 0 ? colorHash.hex(id) : defaultMapPathColor;

      const path = makeMapPath(action.payload.data, {
        lineColor,
        coordinateUnits: action.payload.coordinateUnits,
      });

      const nextData = [...panel.data, path];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForMap(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddSeriesToPlotPanel: {
      if (!isAddSeriesToPlotPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }

      // A signal renders identically twice, so a repeat add is always a
      // caller mistake; guarding here covers every dispatcher (drag-and-drop,
      // AI tools, panel settings).
      if (
        panel.data.some((series) =>
          isSameDataSource(series.data, action.payload.data),
        )
      ) {
        return state;
      }

      // A caller-supplied id that the panel already carries would give two
      // series (and two React rows) the same key, so the action is ignored.
      const suppliedId = action.payload.id;
      if (
        suppliedId !== undefined &&
        panel.data.some((series) => series.id === suppliedId)
      ) {
        return state;
      }

      const id = suppliedId ?? idForSeries();

      const series: PlotSeries = {
        data: action.payload.data,
        id,
        config: {
          lineColor: assignSeriesColor(panel.id, action.payload.data),
        },
        visible: true,
      };

      const nextData = [...panel.data, series];
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForPlot(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.CreatePanel: {
      if (!isCreatePanelAction(action)) {
        return state;
      }

      // A colliding caller-supplied id would silently replace the existing
      // panel while adding a second layout item with the same id, breaking
      // the 1:1 panel-to-layout-item invariant the layout reducers assume.
      if (action.payload.id && (state.panels ?? {})[action.payload.id]) {
        return state;
      }

      const base = { id: action.payload.id ?? crypto.randomUUID() };

      const updatedPanels = {
        ...state.panels,
      };

      const payloadData = action.payload.data ?? [];

      if (action.payload.type === PanelType.Plot) {
        const series: PlotSeries[] = payloadData.map((data) => ({
          config: {
            lineColor: assignSeriesColor(base.id, data),
          },
          data,
          id: idForSeries(),
          visible: true,
        }));
        const panel = {
          ...base,
          type: action.payload.type,
          config: DEFAULT_PLOT_PANEL_CONFIG,
          data: series,
          title: titleForPlot(series),
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.ThreeD) {
        const panel: ThreeDPanelState = {
          ...base,
          title: "",
          type: action.payload.type,
          config: {
            camera: {
              type: "orbit",
              target: { id: "entity0", mesh: "mesh" },
              zoom: {},
            },
            // Copied rather than shared, so one panel's grid edit cannot reach
            // the next panel created from the same default.
            grid: { ...DEFAULT_GRID },
          },
          data: {
            entities: {
              entity0: {
                id: DEFAULT_ENTITY_ID,
                mesh: {
                  type: "sphere",
                  color: "#FF0000",
                  // color: "#DF6888",
                  // color: "#447799",
                  radius: 1,
                },
                path: {
                  color: "#447799",
                  lineWidth: 4,
                },
                position: {
                  forward: 0,
                  forwardScale: 1,
                  up: 0,
                  upScale: 1,
                  right: 0,
                  rightScale: 1,
                },
              },
            },
          },
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Image) {
        const clips = payloadData.map((data) => ({
          data,
          id: idForClip(),
        }));

        const panel: ImagePanelState = {
          ...base,
          type: PanelType.Image,
          config: DEFAULT_IMAGE_PANEL_CONFIG,
          data: clips,
          title: titleForClips(clips),
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Log) {
        const panel = {
          ...base,
          type: action.payload.type,
          data: payloadData,
          title: titleForLogPanel(payloadData),
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Document) {
        const panel = {
          ...base,
          type: action.payload.type,
          data: null,
          title: "",
        };
        updatedPanels[panel.id] = panel;
      } else if (action.payload.type === PanelType.Map) {
        if (action.payload.data === null) {
          const panel = {
            ...base,
            type: action.payload.type,
            data: [],
            title: "",
          };
          updatedPanels[panel.id] = panel;
        } else {
          const path = makeMapPath(action.payload.data, {
            lineColor: defaultMapPathColor,
          });
          const panel = {
            ...base,
            type: action.payload.type,
            data: [path],
            title: titleForMap([path]),
          };
          updatedPanels[panel.id] = panel;
        }
      } else if (action.payload.type === PanelType.RawMessage) {
        const panel = {
          ...base,
          type: action.payload.type,
          data: payloadData,
          title: titleForRawMessagePanel(payloadData),
        };
        updatedPanels[panel.id] = panel;
      } else {
        // Exhaustive check — forces a compile error if a new PanelType is added without a handler
        const _exhaustive: never = action.payload.type;
        throw new Error(`Unhandled panel type: ${String(_exhaustive)}`);
      }

      const nextState: State = {
        ...state,
        panels: updatedPanels,
      };

      // If placement not provided we add the new panel to the left of the root layout
      const placement = action.payload.placement ?? {
        siblingLayout: state.layout,
        orientation: LayoutOrientation.LEFT,
      };

      const layoutItem: LayoutItem = {
        id: base.id,
        isResizing: false,
        relativeSize: 1,
        type: LayoutType.Panel,
      };

      return addItemToLayout(nextState, layoutItem, placement);
    }
    case ActionType.MovePanel: {
      if (!isMovePanelAction(action)) {
        return state;
      }

      const nextState = removeItemFromLayout(state, action.payload.panelLayout);

      // Special case:
      // We currently have the ability to specify a layout as the drop target.
      // As a result, there is a possibility that when we move a panel out of a layout that contains
      // just two children, one of which is the panel we're moving, we delete that layout and merge its
      // remaining child up the tree.
      // So to handle that we set the siblingLayout to be that layout's child if the layout no longer exists
      let { siblingLayout } = action.payload.placement;

      const layout = selectLayoutById(nextState, siblingLayout.id);

      if (layout === undefined) {
        if (isLayoutItem(siblingLayout) === false) {
          if (siblingLayout.children.length === 2) {
            const newSiblingLayout = siblingLayout.children.find(
              (child) => child.id !== action.payload.panelLayout.id,
            );
            if (newSiblingLayout !== undefined) {
              siblingLayout = newSiblingLayout;
            }
          }
        }
      } else {
        // Update siblingLayout with the new size from removing the old panel
        siblingLayout = layout;
      }

      return addItemToLayout(nextState, action.payload.panelLayout, {
        ...action.payload.placement,
        siblingLayout,
      });
    }
    case ActionType.PutImagePanelData: {
      if (!isPutImagePanelDataAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const newClips = [...(panel.data ?? [])];

      // Only add the clip if it's not already in the panel
      if (
        !newClips.some((clip) =>
          isSameDataSource(clip.data, action.payload.data),
        )
      ) {
        newClips.push({
          data: action.payload.data,
          id: idForClip(),
        });
      }

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          // Reset config to defaults if data changes.
          // If previous image was captured upside down or needed to be stretched to fit the canvas,
          // this one may not be.
          config: DEFAULT_IMAGE_PANEL_CONFIG,
          data: newClips,
          title: titleForClips(newClips),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.PutFiles: {
      if (!isPutFilesAction(action)) {
        return state;
      }

      return {
        ...state,
        files: action.payload.files,
        datasources: action.payload.files.map((file) => {
          return {
            type: "file" as const,
            value: file,
          };
        }),
      };
    }
    case ActionType.SetDatasources: {
      if (!isSetDatasourcesAction(action)) {
        return state;
      }

      // Extract files from datasources for backwards compatibility
      const files = action.payload.datasources
        .filter(
          (ds): ds is { type: "file"; value: { fileId: string } } =>
            ds.type === "file",
        )
        .map((ds) => ds.value);

      return {
        ...state,
        datasources: action.payload.datasources,
        files,
      };
    }
    case ActionType.RemoveAllPanels: {
      if (!isRemoveAllPanelsAction(action)) {
        return state;
      }

      // Clean up state potentially left behind in stateful color scale
      cyclingColorScale.reset();

      return {
        ...state,
        panels: { ...INITIAL_STATE.panels },
        layout: {
          ...INITIAL_STATE.layout,
          children: [],
        },
      };
    }

    case ActionType.RemoveClipFromImagePanel: {
      if (!isRemoveClipFromImagePanelAction(action)) {
        return state;
      }

      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isImagePanelState(panel) || !panel.data) {
        return state;
      }

      const nextData =
        panel.data.filter((data) => data.id !== action.payload.clipId) ?? [];

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForClips(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }

    case ActionType.RemoveMessagePathFromLogPanel: {
      if (!isRemoveMessagePathFromLogPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (data) => data.messagePath.id !== action.payload.messagePathId,
      );

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemoveMessagePathFromRawMessagePanel: {
      if (!isRemoveMessagePathFromRawMessagePanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isRawMessagePanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (data) => data.messagePath.id !== action.payload.messagePathId,
      );

      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForRawMessagePanel(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemovePanel: {
      if (!isRemovePanelAction(action)) {
        return state;
      }

      // Remove panel
      const existing = state.panels ?? {};
      const updatedPanels = { ...existing };
      delete updatedPanels[action.payload.panelId];
      // Clean up state potentially left behind in stateful color scale
      cyclingColorScale.resetForPanel(action.payload.panelId);

      const nextState: State = {
        ...state,
        panels: updatedPanels,
      };

      // Find layout for panel id
      const panelLayout = selectLayoutById(nextState, action.payload.panelId);

      if (panelLayout === undefined || !isLayoutItem(panelLayout)) {
        // Should never happen. A corresponding layout should exist for a panel in state
        return nextState;
      }

      return removeItemFromLayout(nextState, panelLayout);
    }
    case ActionType.RemovePathFromMapPanel: {
      if (!isRemovePathFromMapPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (path) => path.id !== action.payload.pathId,
      );
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          data: nextData,
          title: titleForMap(nextData),
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.RemoveSeriesFromPlotPanel: {
      if (!isRemoveSeriesFromPlotPanelAction(action)) {
        return state;
      }
      const panel = (state.panels ?? {})[action.payload.panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }

      const nextData = panel.data.filter(
        (series) => series.id !== action.payload.seriesId,
      );

      // Remove any empty axes from the config.
      let config = panel.config;
      const toRemove = panel.data.find(
        (series) => series.id === action.payload.seriesId,
      );
      if (toRemove) {
        const axis = toRemove.config?.yAxis;
        if (
          axis &&
          config?.extraYAxes &&
          Object.hasOwn(config.extraYAxes, axis)
        ) {
          const willAxisBeEmpty = !!nextData.find(
            (series) => series.config?.yAxis === axis,
          );
          if (willAxisBeEmpty) {
            const { [axis]: _defunct, ...nextExtraAxes } = config.extraYAxes;
            config = {
              ...config,
              extraYAxes: nextExtraAxes,
            };
          }
        }
      }
      const updatedPanels = {
        ...state.panels,
        [panel.id]: {
          ...panel,
          config,
          data: nextData,
          title: titleForPlot(nextData),
        },
      };

      if (nextData.length === 0) {
        // Clean up state potentially left behind in stateful color scale
        cyclingColorScale.resetForPanel(action.payload.panelId);
      }

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.ReplaceState: {
      if (!isReplaceStateAction(action)) {
        return state;
      }
      return action.payload.state;
    }
    case ActionType.ReplacePanelsAndLayout: {
      if (!isReplacePanelsAndLayoutAction(action)) {
        return state;
      }
      return {
        ...state,
        panels: action.payload.panels,
        layout: action.payload.layout,
      };
    }
    case ActionType.ResizeLayouts: {
      if (!isResizeLayoutsAction(action)) {
        return state;
      }

      const { layout1, layout2 } = action.payload;

      const nextState = {
        ...state,
        layout: updateLayout(state.layout, layout1.id, {
          relativeSize: layout1.relativeSize,
        }),
      };

      return {
        ...nextState,
        layout: updateLayout(nextState.layout, layout2.id, {
          relativeSize: layout2.relativeSize,
        }),
      };
    }
    case ActionType.SetAllLayoutsResizing: {
      if (!isSetAllLayoutsResizingAction(action)) {
        return state;
      }
      const { isResizing } = action.payload;
      return setIsLayoutResizing(state, state.layout.children, isResizing);
    }
    case ActionType.SetEventVisibility: {
      if (!isSetEventVisibilityAction(action)) {
        return state;
      }
      const { eventId, isVisible } = action.payload;
      const existingEventConfig = (state.events ?? {})[eventId];

      const updatedEvents = {
        ...state.events,
        [eventId]: {
          ...existingEventConfig,
          isVisible,
        },
      };
      return {
        ...state,
        events: updatedEvents,
      };
    }
    case ActionType.SetEventsVisibility: {
      if (!isSetVisibilityForAllEventsAction(action)) {
        return state;
      }
      const { isVisible, eventIds } = action.payload;
      const updatedEvents = {
        ...state.events,
      };
      eventIds.forEach((eventId) => {
        updatedEvents[eventId] = {
          ...updatedEvents[eventId],
          isVisible,
        };
      });
      return {
        ...state,
        events: updatedEvents,
      };
    }
    case ActionType.SetWorkspaceSettings: {
      if (!isSetWorkspaceSettingsAction(action)) {
        return state;
      }
      return {
        ...state,
        settings: {
          // Spread copies explicitly-`undefined` keys over existing values, and
          // that is how `setWorkspaceSettings` clears a field back to its system
          // default. Filtering `undefined` out of the payload would break it.
          ...state.settings,
          ...action.payload.settings,
        },
      };
    }
    case ActionType.SetImagePanelConfig: {
      if (!isSetImagePanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isImagePanelState(panel)) {
        return state;
      }

      const updatedPanel = {
        ...panel,
        config: {
          // Lowest priority is default config (to respect it not being optional), then existing config, then new config
          rotation: 0,
          stretchToFitCanvas: true,
          ...panel.config,
          ...config,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetLogPanelConfig: {
      if (!isSetLogPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isLogPanelState(panel)) {
        return state;
      }

      // Normalize config: don't persist empty search terms or empty column filters
      const normalizedConfig = { ...config };

      // Remove searchFilter if term is empty or whitespace
      if (
        normalizedConfig.searchFilter !== undefined &&
        normalizedConfig.searchFilter.term.trim() === ""
      ) {
        normalizedConfig.searchFilter = undefined;
      }

      // Remove columnFilters if empty object
      if (
        normalizedConfig.columnFilters !== undefined &&
        Object.keys(normalizedConfig.columnFilters).length === 0
      ) {
        normalizedConfig.columnFilters = undefined;
      }

      const updatedPanel = {
        ...panel,
        config: {
          ...panel.config,
          ...normalizedConfig,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetLayoutResizing: {
      if (!isSetLayoutResizingAction(action)) {
        return state;
      }
      const { layoutIds, isResizing } = action.payload;

      const layouts = layoutIds.map((id) => {
        const layout = selectLayoutById(state, id);
        if (layout === undefined) {
          throw new Error(`Invalid layoutId: ${id}`);
        }
        return layout;
      });

      return setIsLayoutResizing(state, layouts, isResizing);
    }
    case ActionType.SetDataSource: {
      if (!isSetDataSourceAction(action)) {
        return state;
      }
      const { topicData, panelId, segmentId } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || (isPlotPanelState(panel) && segmentId === undefined)) {
        return state;
      }
      let updatedPanel = panel;
      if (isPlotPanelState(panel)) {
        if (Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex((series) => series.id === segmentId);
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev.data, topicData)) {
          return state;
        }

        const next = {
          ...prev,
          data: topicData,
        };
        const series = [...panel.data];
        series.splice(idx, 1, next);
        updatedPanel = {
          ...panel,
          data: series,
          title: titleForPlot(series),
        };
      }
      if (isMapPanelState(panel)) {
        if (!Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex((path) => path.id === segmentId);
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev.data, topicData)) {
          return state;
        }

        const next = {
          ...prev,
          id: idForMap(topicData),
          data: topicData,
        };
        const paths = [...panel.data];
        paths.splice(idx, 1, next);
        updatedPanel = {
          ...panel,
          data: paths,
          title: titleForMap(paths),
        };
      }
      if (isLogPanelState(panel)) {
        if (Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex(
          (data) => data.messagePath.id === segmentId,
        );
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev, topicData)) {
          return state;
        }

        const nextData = [...panel.data];
        nextData.splice(idx, 1, topicData);
        updatedPanel = {
          ...panel,
          data: nextData,
          title: titleForLogPanel(nextData),
        };
      }
      if (isRawMessagePanelState(panel)) {
        if (Array.isArray(topicData)) {
          return state;
        }
        const idx = panel.data.findIndex(
          (data) => data.messagePath.id === segmentId,
        );
        if (idx === -1) {
          return state;
        }
        const prev = panel.data[idx];
        if (isEqual(prev, topicData)) {
          return state;
        }

        const otherEntries = panel.data.filter((_, i) => i !== idx);
        if (
          otherEntries.length > 0 &&
          otherEntries[0].topic.id !== topicData.topic.id
        ) {
          return state;
        }

        const nextData = [...panel.data];
        nextData.splice(idx, 1, topicData);
        updatedPanel = {
          ...panel,
          data: nextData,
          title: titleForRawMessagePanel(nextData),
        };
      }
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPlotPanelConfig: {
      if (!isSetPlotPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updateAxis = (
        original: PlotAxisConfig | undefined,
        updated: Partial<PlotAxisConfig> | undefined,
      ): PlotAxisConfig | undefined => {
        if (original === undefined && updated === undefined) {
          return undefined;
        }
        return {
          ...original,
          ...updated,
        };
      };

      // Only add y axes that are actually used to the panel config.
      const usedYAxes = new Set<PlotYAxis>(["y"]);
      for (const d of panel.data) {
        if (d.config?.yAxis) {
          usedYAxes.add(d.config.yAxis);
        }
      }

      const extraYAxes: Record<string, PlotAxisConfig> = {};
      for (const k of Object.keys(panel.config?.extraYAxes ?? {})) {
        const axis = k as PlotYAxis;
        const original = panel.config?.extraYAxes?.[axis];
        if (usedYAxes.has(axis) && original) {
          extraYAxes[axis] = original;
        }
      }
      if (config.extraYAxes) {
        for (const k of Object.keys(config.extraYAxes)) {
          const axis = k as PlotYAxis;
          const updated = updateAxis(
            panel.config?.extraYAxes?.[axis],
            config.extraYAxes?.[axis],
          );
          if (updated && usedYAxes.has(axis)) {
            extraYAxes[axis] = updated;
          }
        }
      }
      const updatedConfig: PlotPanelConfig = {
        // The defaults lose to the stored config, which loses to the incoming patch. The axes are
        // assigned after the spreads because a spread replaces a stored axis outright, where
        // updateAxis merges the patch into it. xAxis needs this as much as yAxis does: the chart
        // saves its viewport as a bounds-only patch, which would otherwise drop a label the user
        // typed.
        ...DEFAULT_PLOT_PANEL_CONFIG,
        ...panel.config,
        ...config,
        xAxis: updateAxis(panel.config?.xAxis, config?.xAxis),
        yAxis: updateAxis(panel.config?.yAxis, config?.yAxis),
        extraYAxes,
      };

      // xAxis.bounds is a window in the panel's x coordinates, and xAxisAlignment picks those
      // coordinates: unaligned, x counts from the start of the panel's time bounds; aligned, from
      // each series' own zero. Setting, clearing, or re-anchoring the alignment moves those zeros,
      // leaving the saved window measured against coordinates the panel no longer plots, so drop
      // it; useSaveAxisBounds stores a fresh window once the chart has redrawn. An update that
      // omits xAxisAlignment, or re-sends the applied value, leaves both it and the window alone.
      const alignmentChanged =
        Object.hasOwn(config, "xAxisAlignment") &&
        !isEqual(panel.config?.xAxisAlignment, config.xAxisAlignment);
      if (alignmentChanged && updatedConfig.xAxis?.bounds !== undefined) {
        const { bounds: _droppedBounds, ...xAxisWithoutBounds } =
          updatedConfig.xAxis;
        updatedConfig.xAxis = xAxisWithoutBounds;
      }

      const updatedPanel: PlotPanelState = {
        ...panel,
        config: updatedConfig,
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetSeriesConfig: {
      if (!isSetSeriesConfigAction(action)) {
        return state;
      }

      const { panelId, seriesId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];

      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updatedPanel: PlotPanelState = {
        ...panel,
        data: panel.data.map((series): PlotSeries => {
          if (series.id === seriesId) {
            const updatedConfig: PlotSeriesConfig = {
              lineColor: assignSeriesColor(panel.id, series.data),
              ...series.config,
              ...config,
            };
            return {
              ...series,
              config: updatedConfig,
            };
          }
          return series;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetSeriesVisibility: {
      if (!isSetSeriesVisibilityAction(action)) {
        return state;
      }
      const { panelId, seriesId, visible } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isPlotPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((series) => {
          if (series.id === seriesId) {
            return {
              ...series,
              visible,
            };
          }
          return series;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPanelTitle: {
      if (!isSetPanelTitleAction(action)) {
        return state;
      }
      const { panelId, title } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel) {
        return state;
      }

      const updatedPanels = {
        ...state.panels,
        [panelId]: {
          ...panel,
          title,
        },
      };

      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathStyle: {
      if (!isSetPathStyleAction(action)) {
        return state;
      }

      const { panelId, pathId, style } = action.payload;
      const panel = (state.panels ?? {})[panelId];

      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              style: {
                ...path.style,
                ...style,
              },
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathVisibility: {
      if (!isSetPathVisibilityAction(action)) {
        return state;
      }
      const { panelId, pathId, visible } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              visible,
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetPathCoordinateUnits: {
      if (!isSetPathCoordinateUnitsAction(action)) {
        return state;
      }
      const { panelId, pathId, coordinateUnits } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isMapPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data: panel.data.map((path) => {
          if (path.id === pathId) {
            return {
              ...path,
              coordinateUnits,
            };
          }
          return path;
        }),
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetThreeDPanelConfig: {
      if (!isSetThreeDPanelConfigAction(action)) {
        return state;
      }
      const { panelId, config } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        config: {
          ...panel.config,
          ...config,
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.AddThreeDEntity: {
      if (!isAddThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entity } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }
      // Adding an id that is already taken would silently replace the existing
      // entity, discarding its configuration. `CreatePanel` bails on an id
      // collision for the same reason. The registry is the authority on which
      // entities exist, so it is what gets checked.
      const registry = entityRegistryOf(panel);
      if (registry[entity.id]) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        config: {
          ...panel.config,
          // Declared as a root; the parent picker sets a link afterwards.
          entities: { ...registry, [entity.id]: {} },
        },
        data: {
          entities: { ...panel.data.entities, [entity.id]: entity },
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.UpdateThreeDEntity: {
      if (!isUpdateThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entityId, updates } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }

      const updatedPanel: ThreeDPanelState = {
        ...panel,
        data: {
          entities: {
            ...panel.data.entities,
            [entityId]: { ...panel.data.entities[entityId], ...updates },
          },
        },
      };
      const updatedPanels = {
        ...state.panels,
        [panel.id]: updatedPanel,
      };
      return {
        ...state,
        panels: updatedPanels,
      };
    }
    case ActionType.SetDocumentPanelFile: {
      if (!isSetDocumentPanelFileAction(action)) {
        return state;
      }
      const { panelId, data } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isDocumentPanelState(panel)) {
        return state;
      }
      const updatedPanel = {
        ...panel,
        data,
        title: data.fileName,
      };
      return {
        ...state,
        panels: {
          ...state.panels,
          [panel.id]: updatedPanel,
        },
      };
    }
    case ActionType.CreateDocumentPanelWithFile: {
      if (!isCreateDocumentPanelWithFileAction(action)) {
        return state;
      }
      const id = crypto.randomUUID();
      const panel: DocumentPanelState = {
        id,
        type: PanelType.Document,
        data: action.payload.data,
        title: action.payload.data.fileName,
      };
      const nextState: State = {
        ...state,
        panels: {
          ...state.panels,
          [id]: panel,
        },
      };
      const placement = action.payload.placement ?? {
        siblingLayout: state.layout,
        orientation: LayoutOrientation.LEFT,
      };
      const layoutItem: LayoutItem = {
        id,
        isResizing: false,
        relativeSize: 1,
        type: LayoutType.Panel,
      };
      return addItemToLayout(nextState, layoutItem, placement);
    }
    case ActionType.SetThreeDModel: {
      if (!isSetThreeDModelAction(action)) {
        return state;
      }
      const { panelId, entityId, file, source, joints, jointValues } =
        action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }
      // The entity has to exist, or the panel would hold components for an
      // entity its registry never declared and nothing would draw them.
      if (!entityRegistryOf(panel)[entityId]) {
        return state;
      }

      // The camera is left exactly as it was found, including its *type*.
      //
      // An import used to swap an orbit camera targeting this entity for a
      // third-person one framing the model it had just measured. It framed
      // nicely and cost tracking: a third-person camera's focus is a fixed world
      // point, so a base bound to an odometry topic drove straight out of frame
      // and the user had to pan after it or toggle back to orbit. Choosing that
      // trade on the user's behalf, silently, at the moment they added a robot,
      // is not a choice an import gets to make. An orbit camera can now be
      // pointed at one node of the model instead (`camera.target.node`), which
      // is the framing control this was standing in for, and it keeps tracking.
      const updatedPanel: ThreeDPanelState = {
        ...panel,
        data: {
          entities: {
            ...panel.data.entities,
            // Replaces whatever this entity drew before, rather than merging:
            // the joints belong to the file, so keeping a previous model's
            // alongside a new one's would list joints the file does not have.
            // Any `jointValues` go with them, for the same reason — which is
            // why the action carries the values to keep rather than the reducer
            // preserving them. What survives an import is decided by
            // `withDefaultBindings`, in one place, against the new file's joints.
            [entityId]: {
              ...(panel.data.entities[entityId] ?? { id: entityId }),
              mesh: {
                type: "model",
                file,
                ...(source ? { source } : {}),
                ...(joints && joints.length > 0 ? { joints } : {}),
                ...(jointValues && Object.keys(jointValues).length > 0
                  ? { jointValues }
                  : {}),
              },
            },
          },
        },
      };
      return {
        ...state,
        panels: { ...state.panels, [panel.id]: updatedPanel },
      };
    }
    case ActionType.RenameThreeDEntity: {
      if (!isRenameThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, fromId, toId } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }
      const registry = entityRegistryOf(panel);
      if (!registry[fromId] || toId.trim() === "" || fromId === toId) {
        return state;
      }
      if (registry[toId]) {
        // Would clobber another entity.
        return state;
      }

      // Both records are rebuilt key by key rather than delete-then-add, so the
      // renamed entity keeps its position. Insertion order is load-bearing:
      // `resolveFallbackEntity` and `updateSceneConfig` both fall back to the
      // first entity, and a rename should not silently re-aim the camera.
      //
      // The registry references the id twice over — once as this entity's own
      // entry, and once per child naming it as `parent` — so both have to move.
      const renamedRegistry: EntityRegistry = {};
      for (const [id, declaration] of Object.entries(registry)) {
        const moved =
          declaration.parent === fromId
            ? { ...declaration, parent: toId }
            : declaration;
        renamedRegistry[id === fromId ? toId : id] = moved;
      }

      const renamedComponents: Record<string, ThreeDEntity> = {};
      for (const [id, entity] of Object.entries(panel.data.entities)) {
        if (id === fromId) {
          renamedComponents[toId] = { ...entity, id: toId };
        } else {
          renamedComponents[id] = entity;
        }
      }

      // The camera targets an entity by id, so it has to follow the rename or
      // it would dangle and the panel would stop following anything.
      const camera = panel.config?.camera;
      const updatedPanel: ThreeDPanelState = {
        ...panel,
        config: {
          ...panel.config,
          entities: renamedRegistry,
          ...(camera?.type === "orbit" && camera.target.id === fromId
            ? { camera: { ...camera, target: { ...camera.target, id: toId } } }
            : {}),
        },
        data: { entities: renamedComponents },
      };
      return {
        ...state,
        panels: { ...state.panels, [panel.id]: updatedPanel },
      };
    }
    case ActionType.RemoveThreeDEntity: {
      if (!isRemoveThreeDEntityAction(action)) {
        return state;
      }
      const { panelId, entityId, cascade } = action.payload;
      const panel = (state.panels ?? {})[panelId];
      if (!panel || !isThreeDPanelState(panel)) {
        return state;
      }
      const registry = entityRegistryOf(panel);
      if (!registry[entityId]) {
        return state;
      }

      const doomed = cascade
        ? new Set([entityId, ...descendantIds(registry, entityId)])
        : new Set([entityId]);

      // The panel must keep at least one entity, so a removal that would empty
      // it is refused rather than producing state that fails to serialize.
      if (doomed.size >= Object.keys(registry).length) {
        return state;
      }

      // Children of a non-cascading removal splice up to their grandparent, or
      // become roots when that parent is itself absent, so nothing is orphaned.
      const promotedParent = registry[entityId].parent;
      const updatedRegistry: EntityRegistry = {};
      for (const [id, declaration] of Object.entries(registry)) {
        if (doomed.has(id)) {
          continue;
        }
        const parent = declaration.parent;
        if (parent === undefined || !doomed.has(parent)) {
          updatedRegistry[id] = declaration;
        } else if (promotedParent === undefined) {
          // A root is expressed by having no `parent` key at all, not by a null
          // one — an explicit `undefined` is not `lodash.isEqual` to an absent
          // key, and the ECS diffs the declaration on that.
          updatedRegistry[id] = {};
        } else {
          updatedRegistry[id] = { ...declaration, parent: promotedParent };
        }
      }

      const updatedComponents: Record<string, ThreeDEntity> = {};
      for (const [id, entity] of Object.entries(panel.data.entities)) {
        if (!doomed.has(id)) {
          updatedComponents[id] = entity;
        }
      }

      // Re-aim a camera left pointing at something that no longer exists.
      const camera = panel.config?.camera;
      const updatedPanel: ThreeDPanelState = {
        ...panel,
        config: {
          ...panel.config,
          entities: updatedRegistry,
          ...(camera?.type === "orbit" && doomed.has(camera.target.id)
            ? {
                camera: {
                  ...camera,
                  target: {
                    ...camera.target,
                    id: Object.keys(updatedRegistry)[0],
                  },
                },
              }
            : {}),
        },
        data: { entities: updatedComponents },
      };
      return {
        ...state,
        panels: { ...state.panels, [panel.id]: updatedPanel },
      };
    }
    default:
      return state;
  }
}
